import { type FC, useState } from "react";
import { type LinearCstic, type LinearCsticValue } from "../../models";
import LabeledInput from "../../../../common/components/LabeledInput";
import { useBooleanState } from "../../../../common/hooks/useBooleanState";
import Modal from "../../../../common/components/Modal";
import { Col, Row } from "../../../../common/components/Layout";
import LinearTile from "../LinearTile";
import { useDispatch } from "react-redux";
import { updateCsticValues } from "../../redux/actions";
import { type SubstepType } from "../../dtos";
import { IconClickable } from "../../../../common/components/icon/IconClickable";
import { IconType } from "../../../../common/components/icon/IconType";
import { IconSize } from "../../../../common/components/icon/IconSize";
import { toMapFn } from "../../../../utils/reducers";
import LanguageAwareExternalContent from "../LanguageAwareExternalContent";
import TabContext from "../../../../common/components/Tabs/TabContext";
import TabList from "../../../../common/components/Tabs/TabList";
import Tab from "../../../../common/components/Tabs/Tab";
import TabPanel from "../../../../common/components/Tabs/TabPanel";
import { formatCsticsToSet } from "../../utils";
import classNames from "classnames";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { useTranslation } from "react-i18next";
import { useConfiguratorType } from "~/Configurators/common/useConfiguratorType";
import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import { LinearTileV2 } from "~/Configurators/Shaft/substeps/LinearTileV2";
import { KmatType } from "~/Configurators/Linear/kmatType";

interface TabConfig {
  tabName: string;
  values: string[];
}

interface Props {
  className?: string;
  carriageIndex: number;
  cstic: LinearCstic | undefined;
  substep: SubstepType;
  disabled?: boolean;
  tabConfig?: TabConfig[];
  hasError?: boolean;
  preClick?: () => void;
  selectionInfo?: string;
}

const PopupSelectionCstic: FC<Props> = ({
  cstic,
  substep,
  carriageIndex,
  className,
  disabled,
  tabConfig,
  hasError,
  preClick,
  selectionInfo,
}) => {
  const { val: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  if (!cstic) {
    return null;
  }

  const performClick = () => {
    if (preClick) {
      preClick();
    }
    openModal();
  };

  const selectedCsticVal = cstic.values.filter((csticVal) => csticVal.key === cstic.value)[0];
  const selectedValue = selectedCsticVal?.label ?? "";

  const label = cstic.label || "MISSING LABEL";

  const spanClasses = classNames("scha__lc__external-label", {
    "f-disabled-text": disabled,
  });

  return (
    <div className={className}>
      <LabeledInput
        label={<LanguageAwareExternalContent content={label} loadedClassname={"scha__lc__external-label"} />}
        className={"mb-3"}
        hasError={hasError}
        info={selectionInfo}
      >
        <div className={"title-medium mt-1 d-flex flex-align-center"}>
          {selectedCsticVal?.standard && <LinearTileV2.StandardIndicator omitText className="icon-margin" />}
          <span className={"icon-margin"}>
            <LanguageAwareExternalContent content={selectedValue} loadedClassname={spanClasses} />
          </span>
          <IconClickable type={IconType.Pencil} size={IconSize.FONT_20} onClick={performClick} disabled={disabled} />
        </div>
      </LabeledInput>

      {isModalOpen && (
        <SelectionModal
          close={closeModal}
          tabConfig={tabConfig}
          cstic={cstic}
          carriageIndex={carriageIndex}
          substep={substep}
        />
      )}
    </div>
  );
};
export default PopupSelectionCstic;

interface ModalProps {
  close: () => void;
  tabConfig?: TabConfig[];
  cstic: LinearCstic;
  carriageIndex: number;
  substep: SubstepType;
}
const SelectionModal = ({ close, tabConfig, cstic, carriageIndex, substep }: ModalProps) => {
  const label = cstic.label || "MISSING LABEL";

  const { t } = useTranslation();
  const debugLanguage = isDebugLanguageEnabled();

  const configuratorType = useConfiguratorType();
  const isOldConfigurator = configuratorType === ConfiguratorType.System;

  const dispatch = useDispatch();
  const buildTiles = (values: LinearCsticValue[]) => {
    return (
      <Row className={"d-flex pt-30"}>
        {values.map((val, i) => (
          <Col key={val.value} size={3} className={"mb-30"}>
            <LinearTile
              key={val.value}
              isStandard={val.standard}
              imageUrl={val.imageUrl}
              onClickHandler={() => {
                dispatch(
                  updateCsticValues(
                    [
                      {
                        type: KmatType.SubCarriage,
                        key: cstic.key,
                        value: val.value,
                        index: carriageIndex,
                      },
                    ],
                    substep,
                  ),
                );
                close();
              }}
              info={val.info}
              isPicked={val.value === cstic.value}
              title={debugLanguage ? formatCsticsToSet({ [cstic.key]: val.value }) : val.label}
            />
          </Col>
        ))}
      </Row>
    );
  };

  const [activeTabIndex, setActiveTab] = useState<string>("0");

  let renderTabLabel;
  if (isOldConfigurator) {
    renderTabLabel = (tab: TabConfig) => {
      return tab.tabName?.startsWith("http") ? <LanguageAwareExternalContent content={tab.tabName} /> : t(tab.tabName);
    };
  } else {
    renderTabLabel = (tab: TabConfig) => {
      return <LanguageAwareExternalContent content={tab.tabName} />;
    };
  }

  return (
    <Modal onClose={close}>
      <p className={"title-medium mt-0 mb-2"}>
        <LanguageAwareExternalContent content={label} loadedClassname={"scha__lc__external-label"} />
      </p>
      <p className={"mt-0 mb-30"}>
        <LanguageAwareExternalContent content={cstic.description} loadedClassname={"scha__lc__external-label"} />
      </p>
      {(!tabConfig || tabConfig.length === 0) && <div className={""}>{buildTiles(cstic.values)}</div>}
      {tabConfig && tabConfig.length > 0 && (
        <div>
          <TabContext value={activeTabIndex} nestedValue={""}>
            <TabList onChange={(e, val) => setActiveTab(val)} variant="fullWidth">
              {tabConfig.map((tab, index) => (
                <Tab key={index} value={index + ""} label={renderTabLabel(tab)} />
              ))}
            </TabList>

            {tabConfig.map((tab, index) => (
              <TabPanel key={index} value={index + ""} preserveChildren={true}>
                {buildTiles(splitLinearCsticValues(cstic.values, tab))}
              </TabPanel>
            ))}
          </TabContext>
        </div>
      )}
    </Modal>
  );
};

const splitLinearCsticValues = (linearCsticValues: LinearCsticValue[], tabConfig: TabConfig) => {
  const valuesMap = toMapFn(
    tabConfig.values,
    (val) => val,
    () => true,
  );
  return linearCsticValues.filter((csticVal) => valuesMap[csticVal.key]);
};
